import Cookies from "js-cookie";
import Layer from "ol/layer/Layer";
import OLEEditor from "ole/src/editor";

import configs from "../../config";
import { authorizedFetch, fetchData, getIsPublic } from "../../utils/actions";
import {
  LS_CD_KEY,
  LS_KML_KEY,
  LS_ORGANIZATION_KEY,
  MAPSET_LAYOUT_FILENAME,
  MAPSET_LAYOUT_MAP,
  MAPSET_LAYOUT_MAP_HEIGHT,
  MAPSET_LAYOUT_MAP_SCALE,
  MAPSET_LAYOUT_MAP_WIDTH,
  MAPSET_LAYOUT_TITLE,
  MAX_ANON_USE,
  MAX_MAPS_REACHED_DIALOG_NAME,
  READ_ONLY_DIALOG_NAME,
  STATE_SAVE,
  STATE_SAVED,
  STATE_SAVING,
  WARN_DIALOG_NAME,
} from "../../utils/constants";
import download from "../../utils/download";
import getExportSizeFromExportCoordinates from "../../utils/getExportSizeFromExportCoordinates";
import { getIconCategoriesWithUrl } from "../../utils/getIconCategories";
import getLayoutScreenshotUrl from "../../utils/getLayoutScreenshotUrl";
import getMaxMaps from "../../utils/getMaxMaps";
import { hasRightSetDefaults } from "../../utils/hasRight";
import { getLayoutVariableValue } from "../../utils/processLayouts";
import writeKml from "../../utils/writeKml";
import { refreshMe } from "../oidc/actions";

import {
  FetchDetailsError,
  IconCategory,
  Layout,
  LayoutConfig,
  MetaKml,
  Organization,
  PaginatedResults,
  PlanInfo,
  User,
} from "./propTypes";

import type { Feature } from "ol";

import type {
  AppDomainConfig,
  AppState,
  DialogPosition,
  DialogSize,
  ExportSize,
  Settings,
} from "../../envConfig";
import type {
  AppAction,
  AppDispatch,
  AppThunk,
  RootState,
  ThunkExtraArg,
} from "../store";

export type { RootState } from "../store";

/** Signature of `redux-thunk`'s `getState` for this app. */
export type GetState = () => RootState;

/** Signature of `redux-thunk`'s `dispatch` for this app. */
export type Dispatch = AppDispatch;

export const AppActionType = {
  DeleteKml: "DELETE_KML",
  FetchError: "FETCH_ERROR",
  GetKml: "GET_KML",
  Redo: "REDO",
  ResetBusFilters: "RESET_BUS_FILTERS",
  SaveKml: "SAVE_KML",
  SetActiveButton: "SET_ACTIVE_BUTTON",
  SetAnonCount: "SET_ANON_COUNT",
  SetAppReady: "SET_APP_READY",
  SetBboxInteracting: "SET_BBOX_INTERACTING",
  SetBusFilters: "SET_BUS_FILTERS",
  SetBusLayers: "SET_BUS_LAYERS",
  SetConfig: "SET_CONFIG",
  SetConfigs: "SET_CONFIGS",
  SetCurrentGroupControls: "SET_CURRENT_GROUP_CONTROLS",
  SetCurrentSidebarWidth: "SET_CURRENT_SIDEBAR_WIDTH",
  SetDialogPinToRight: "SET_DIALOG_PIN_TO_RIGHT",
  SetDialogPosition: "SET_DIALOG_POSITION",
  SetDialogSize: "SET_DIALOG_SIZE",
  SetDialogVisible: "SET_DIALOG_VISIBLE",
  SetDrawControlActive: "SET_DRAW_CONTROL_ACTIVE",
  SetDrawInfo: "SET_DRAW_INFO",
  SetDrawinfoError: "SET_DRAWINFO_ERROR",
  SetDrawInfos: "SET_DRAW_INFOS",
  SetDrawing: "SET_DRAWING",
  SetEditor: "SET_EDITOR",
  SetExportBboxLockedToMap: "SET_EXPORT_BBOX_LOCKED_TO_MAP",
  SetExportBtnActive: "SET_EXPORT_BTN_ACTIVE",
  SetExportCoordinates: "SET_EXPORT_COORDINATES",
  SetExportFormat: "SET_EXPORT_FORMAT",
  SetExportLayout: "SET_EXPORT_LAYOUT",
  SetExportLayoutConfig: "SET_EXPORT_LAYOUT_CONFIG",
  SetExportLayouts: "SET_EXPORT_LAYOUTS",
  SetExportNorthArrow: "SET_EXPORT_NORTH_ARROW",
  SetExportQrCode: "SET_EXPORT_QR_CODE",
  SetExportScale: "SET_EXPORT_SCALE",
  SetExportSelect: "SET_EXPORT_SELECT",
  SetExportSize: "SET_EXPORT_SIZE",
  SetExportType: "SET_EXPORT_TYPE",
  SetGeolocationActive: "SET_GEOLOCATION_ACTIVE",
  SetIconCategories: "SET_ICON_CATEGORIES",
  SetIconsGroup: "SET_ICONS_GROUP",
  SetInspectMode: "SET_INSPECT_MODE",
  SetKmlHistory: "SET_KML_HISTORY",
  SetOrganization: "SET_ORGANIZATION",
  SetOrganizations: "SET_ORGANIZATIONS",
  SetParent: "SET_PARENT",
  SetReadonlyMode: "SET_READONLY_MODE",
  SetSaveConfirm: "SET_SAVE_CONFIRM",
  SetSelectedFeature: "SET_SELECTED_FEATURE",
  SetSelectedFeatures: "SET_SELECTED_FEATURES",
  SetSelectedPlan: "SET_SELECTED_PLAN",
  SetShortenedReadLinks: "SET_SHORTENED_READ_LINKS",
  SetShortenedWriteLinks: "SET_SHORTENED_WRITE_LINKS",
  SetSidebarOpen: "SET_SIDEBAR_OPEN",
  SetTramLayers: "SET_TRAM_LAYERS",
  SetUsingHistory: "SET_USING_HISTORY",
  TestOpenid: "TEST_OPENID",
  Undo: "UNDO",
  UpsertDrawInfo: "UPSERT_DRAW_INFO",
} as const;

export type AppActionType = (typeof AppActionType)[keyof typeof AppActionType];

export const TEST_OPENID = AppActionType.TestOpenid;
export const GET_KML = AppActionType.GetKml;
export const SAVE_KML = AppActionType.SaveKml;
export const DELETE_KML = AppActionType.DeleteKml;
export const SET_SIDEBAR_OPEN = AppActionType.SetSidebarOpen;
export const SET_DIALOG_VISIBLE = AppActionType.SetDialogVisible;
export const SET_DIALOG_PIN_TO_RIGHT = AppActionType.SetDialogPinToRight;
export const SET_DIALOG_POSITION = AppActionType.SetDialogPosition;
export const SET_DIALOG_SIZE = AppActionType.SetDialogSize;
export const SET_ACTIVE_BUTTON = AppActionType.SetActiveButton;
export const SET_SELECTED_FEATURES = AppActionType.SetSelectedFeatures;
export const SET_SELECTED_FEATURE = AppActionType.SetSelectedFeature;
export const SET_ICONS_GROUP = AppActionType.SetIconsGroup;
export const SET_EXPORT_SCALE = AppActionType.SetExportScale;
export const SET_EXPORT_SIZE = AppActionType.SetExportSize;
export const SET_EXPORT_SELECT = AppActionType.SetExportSelect;
export const SET_EXPORT_NORTH_ARROW = AppActionType.SetExportNorthArrow;
export const SET_EXPORT_COORDINATES = AppActionType.SetExportCoordinates;
export const SET_EXPORT_QR_CODE = AppActionType.SetExportQrCode;
export const SET_EXPORT_LAYOUT = AppActionType.SetExportLayout;
export const SET_EXPORT_LAYOUTS = AppActionType.SetExportLayouts;
export const SET_SHORTENED_READ_LINKS = AppActionType.SetShortenedReadLinks;
export const SET_SHORTENED_WRITE_LINKS = AppActionType.SetShortenedWriteLinks;
export const FETCH_ERROR = AppActionType.FetchError;
export const SET_SAVE_CONFIRM = AppActionType.SetSaveConfirm;
export const SET_BUS_LAYERS = AppActionType.SetBusLayers;
export const SET_TRAM_LAYERS = AppActionType.SetTramLayers;
export const SET_BUS_FILTERS = AppActionType.SetBusFilters;
export const RESET_BUS_FILTERS = AppActionType.ResetBusFilters;
export const SET_KML_HISTORY = AppActionType.SetKmlHistory;
export const SET_USING_HISTORY = AppActionType.SetUsingHistory;
export const SET_CONFIG = AppActionType.SetConfig;
export const SET_CONFIGS = AppActionType.SetConfigs;
export const UNDO = AppActionType.Undo;
export const REDO = AppActionType.Redo;
export const SET_PARENT = AppActionType.SetParent;
export const SET_SELECTED_PLAN = AppActionType.SetSelectedPlan;
export const SET_DRAW_INFO = AppActionType.SetDrawInfo;
export const SET_DRAW_INFOS = AppActionType.SetDrawInfos;
export const UPSERT_DRAW_INFO = AppActionType.UpsertDrawInfo;
export const SET_INSPECT_MODE = AppActionType.SetInspectMode;
export const SET_ANON_COUNT = AppActionType.SetAnonCount;
export const SET_EXPORT_FORMAT = AppActionType.SetExportFormat;
export const SET_DRAWINFO_ERROR = AppActionType.SetDrawinfoError;
export const SET_EXPORT_LAYOUT_CONFIG = AppActionType.SetExportLayoutConfig;
export const SET_BBOX_INTERACTING = AppActionType.SetBboxInteracting;
export const SET_DRAWING = AppActionType.SetDrawing;
export const SET_DRAW_CONTROL_ACTIVE = AppActionType.SetDrawControlActive;
export const SET_EDITOR = AppActionType.SetEditor;
export const SET_EXPORT_BBOX_LOCKED_TO_MAP =
  AppActionType.SetExportBboxLockedToMap;
export const SET_CURRENT_SIDEBAR_WIDTH = AppActionType.SetCurrentSidebarWidth;
export const SET_CURRENT_GROUP_CONTROLS = AppActionType.SetCurrentGroupControls;
export const SET_EXPORT_TYPE = AppActionType.SetExportType;
export const SET_APP_READY = AppActionType.SetAppReady;
export const SET_GEOLOCATION_ACTIVE = AppActionType.SetGeolocationActive;
export const SET_EXPORT_BTN_ACTIVE = AppActionType.SetExportBtnActive;
export const SET_READONLY_MODE = AppActionType.SetReadonlyMode;
export const SET_ORGANIZATION = AppActionType.SetOrganization;
export const SET_ORGANIZATIONS = AppActionType.SetOrganizations;

// Increase the anonymouse use counter of a specifiy feature determined by its id.
export const increaseAnonUse = (id: string) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      app: { countAnonUse = {} },
      oidc: { user },
    } = getState();

    const count = countAnonUse[id] || 0;

    if (user || count > MAX_ANON_USE) {
      return null;
    }

    return dispatch({
      data: { ...countAnonUse, [id]: count + 1 },
      type: SET_ANON_COUNT,
    });
  };
};

export const setDrawControlActive = (data: boolean): AppAction => {
  return {
    data,
    type: SET_DRAW_CONTROL_ACTIVE,
  };
};
export const setDrawing = (data: boolean): AppAction => {
  return { data, type: SET_DRAWING };
};
export const setExportScale = (data: number): AppAction => {
  return { data, type: SET_EXPORT_SCALE };
};

export const setExportSize = (data?: ExportSize) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { exportSize } = getState().app;
    if (JSON.stringify(exportSize) !== JSON.stringify(data)) {
      dispatch({
        data,
        type: SET_EXPORT_SIZE,
      });
    }
  };
};

export const setExportSelect = (data: boolean): AppAction => {
  return { data, type: SET_EXPORT_SELECT };
};
export const setExportNorthArrow = (data: boolean): AppAction => {
  return {
    data,
    type: SET_EXPORT_NORTH_ARROW,
  };
};

export const setExportQrCode = (data: number | undefined): AppAction => {
  return {
    data,
    type: SET_EXPORT_QR_CODE,
  };
};

export const setExportCoordinates = (data: null | number[][]) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { exportCoordinates } = getState().app;
    const { olMap } = getState().map;
    const flatData = data?.flat();
    const flatExportCoordinates = exportCoordinates?.flat();

    if (JSON.stringify(flatExportCoordinates) !== JSON.stringify(flatData)) {
      let mustBePropagate = true;
      if (
        flatExportCoordinates &&
        flatData &&
        flatExportCoordinates.length === flatData.length
      ) {
        // It happens that when we move the map that the export coordinates changes slightly +/- 0.00001
        // We don't propagate the change if the difference is less than 0.00001, to avoid changing
        // the parameter in the url and confusing the user.
        mustBePropagate = flatData.some((coord, idx) => {
          return Math.abs(flatExportCoordinates[idx] - coord) > 0.00001;
        });
      }
      let thunk;
      if (mustBePropagate) {
        thunk = dispatch({
          data,
          type: SET_EXPORT_COORDINATES,
        });
      }
      const size = getExportSizeFromExportCoordinates(data, olMap);
      dispatch(setExportSize(size));
      return Promise.resolve(thunk);
    }
    return Promise.resolve();
  };
};

export const setExportFormat = (data: string): AppAction => {
  return {
    data,
    type: SET_EXPORT_FORMAT,
  };
};

export const setExportLayouts = (data: Layout[]): AppAction => {
  return {
    data,
    type: SET_EXPORT_LAYOUTS,
  };
};

export const setExportLayoutConfig = (obj: LayoutConfig) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      app: { exportLayoutConfig },
    } = getState();
    // Update layout only when it has changed, we test the uri which is the identifier of a layout.
    // TODO: according to types the exportLayoutCOnfig has never a layout property only the value sof variables
    // So the test is always false.
    // if (obj.layout?.uri === exportLayoutConfig?.layout?.uri) {
    //   delete obj.layout;
    // }

    dispatch({
      data: {
        ...(exportLayoutConfig || {}),
        ...obj,
      },
      type: SET_EXPORT_LAYOUT_CONFIG,
    });
  };
};

export const setExportLayout = (data: Layout) => {
  return (
    dispatch: Dispatch,
    getState: GetState,

    { t }: ThunkExtraArg,
  ) => {
    const {
      app: { drawInfo, exportFormat, exportScale, exportSelect, exportSize },
    } = getState();

    if (data) {
      const {
        properties: { formats, mapHeight: h, mapRatio, mapWidth: w, scales },
        variables,
      } = data;
      const dfltFormat = (formats || [])[0];
      const dfltScale = (scales || [])[0];

      const newExportConfig =
        variables &&
        Object.entries(variables)?.reduce((acc, [key, variable]) => {
          if (variable.type === "screenshoturl") {
            return acc;
          }
          return {
            ...acc,
            [key]: getLayoutVariableValue(
              variable.defaultValue,
              t,
              drawInfo?.name,
            ),
          };
        }, {});

      if (newExportConfig) {
        dispatch(setExportLayoutConfig(newExportConfig));
      }

      if ((!w || !h) && !exportSelect) {
        dispatch(setExportSize());
      }

      // If the current size doesn't respect the image ratio we apply the good ratio.
      if (
        w &&
        h &&
        (!exportSize || Math.abs(exportSize[0] / exportSize[1] - w / h) > 0.01)
      ) {
        if (!exportSize) {
          dispatch(setExportSize([w, h]));
        } else if (Math.abs(exportSize[0] / exportSize[1] - w / h) > 0.01) {
          dispatch(setExportSize([exportSize[0], exportSize[0] / mapRatio]));
        }
      }

      // We set the default format if the current one is not supported
      if (
        dfltFormat &&
        (!exportFormat ||
          !(formats || []).find((value) => {
            return exportFormat === value;
          }))
      ) {
        dispatch(setExportFormat(dfltFormat));
      }

      // We set the default format if the current one is not supported
      if (
        dfltScale &&
        (!exportScale ||
          !(scales || []).find((value) => {
            return exportScale === value;
          }))
      ) {
        dispatch(setExportScale(dfltScale));
      }
    }

    return dispatch({ data, type: SET_EXPORT_LAYOUT });
  };
};

export const setSelectedFeature = (data?: Feature): AppAction => {
  return {
    data,
    type: SET_SELECTED_FEATURE,
  };
};

export const setIconsGroup = (data?: Feature[]) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { iconsGroup: prevIconsGroup } = getState().app;

    // Make sure the array is really not the same
    if (
      prevIconsGroup?.length !== data?.length ||
      prevIconsGroup?.find((feature, idx) => {
        return feature !== data?.[idx];
      }) ||
      data?.find((feature, idx) => {
        return feature !== prevIconsGroup?.[idx];
      })
    ) {
      dispatch({
        data,
        type: SET_ICONS_GROUP,
      });
    }
  };
};

export const setSelectedFeatures = (data?: Feature[]) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { selectedFeatures: prevSelectedFeatures } = getState().app;

    // Make sure the array is really not the same
    if (
      prevSelectedFeatures?.length !== data?.length ||
      prevSelectedFeatures?.find((feature, idx) => {
        return feature !== data?.[idx];
      }) ||
      data?.find((feature, idx) => {
        return feature !== prevSelectedFeatures?.[idx];
      })
    ) {
      dispatch({
        data,
        type: SET_SELECTED_FEATURES,
      });
    }
  };
};

export const setBboxInteracting = (data?: boolean): AppAction => {
  return {
    data,
    type: SET_BBOX_INTERACTING,
  };
};

export const setDialogVisible = (
  dialogName?: string,
  dialogData?: boolean | Record<string, unknown>,
) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      app: { dialogData: currentDialogData, dialogVisible },
    } = getState();
    const openDialogNameOnClose = currentDialogData?.openDialogNameOnClose;
    const nextDialogName =
      dialogName ||
      (typeof openDialogNameOnClose === "string"
        ? openDialogNameOnClose
        : undefined);

    if (nextDialogName && nextDialogName !== dialogVisible) {
      increaseAnonUse(nextDialogName)(dispatch, getState);
    }

    return dispatch({
      data: nextDialogName && {
        dialogData,
        dialogVisible: nextDialogName,
      },
      type: SET_DIALOG_VISIBLE,
    });
  };
};

export const setDialogPinToRight = (data?: boolean): AppAction => {
  return {
    data,
    type: SET_DIALOG_PIN_TO_RIGHT,
  };
};

export const setDialogPosition = (data: DialogPosition): AppAction => {
  return {
    data,
    type: SET_DIALOG_POSITION,
  };
};

export const setDialogSize = (data: DialogSize): AppAction => {
  return {
    data,
    type: SET_DIALOG_SIZE,
  };
};

export const setActiveButton = (data?: string): AppAction => {
  return {
    data,
    type: SET_ACTIVE_BUTTON,
  };
};

export const setSidebarOpen = (data?: boolean): AppAction => {
  return {
    data,
    type: SET_SIDEBAR_OPEN,
  };
};

export const deleteKml = (data?: string): AppAction => {
  return {
    data,
    type: DELETE_KML,
  };
};

export const setShortenedReadLinks = (
  data?: Record<string, string>,
): AppAction => {
  return {
    data,
    type: SET_SHORTENED_READ_LINKS,
  };
};

export const setShortenedWriteLinks = (
  data?: Record<string, string>,
): AppAction => {
  return {
    data,
    type: SET_SHORTENED_WRITE_LINKS,
  };
};

export const setFetchError = (
  data: null | string,
  action?: null | string,
): AppAction => {
  return {
    action,
    data,
    type: FETCH_ERROR,
  };
};

let saveConfirmTimeout: NodeJS.Timeout;
let saveConfirmTimeout2: NodeJS.Timeout;
export const setSaveConfirm = (
  data: string,
  onStateSaved?: () => void,
  onStateSave?: () => void,
): AppThunk => {
  return (dispatch: Dispatch) => {
    clearTimeout(saveConfirmTimeout);
    clearTimeout(saveConfirmTimeout2);

    if (data === STATE_SAVED) {
      // We put back the succes state during 1 seconds
      saveConfirmTimeout = setTimeout(() => {
        dispatch({
          data,
          type: SET_SAVE_CONFIRM,
        });
        onStateSaved?.();
      }, 1000);
      // We put back the original state when nothing happens after 2000 ms
      saveConfirmTimeout = setTimeout(() => {
        dispatch(setSaveConfirm(STATE_SAVE));
        onStateSave?.();
      }, 3500);
      return;
    }

    dispatch({
      data,
      type: SET_SAVE_CONFIRM,
    });
  };
};

export const setBusLayers = (data): AppAction => {
  return {
    data,
    type: SET_BUS_LAYERS,
  };
};

export const setTramLayers = (data): AppAction => {
  return {
    data,
    type: SET_TRAM_LAYERS,
  };
};

export const setBusFilters = (data): AppAction => {
  return {
    data,
    type: SET_BUS_FILTERS,
  };
};

export const resetBusFilters = (data): AppAction => {
  return {
    data,
    type: RESET_BUS_FILTERS,
  };
};

export const setKmlHistory = (data): AppAction => {
  return {
    data,
    type: SET_KML_HISTORY,
  };
};
export const setAppReady = (data): AppAction => {
  return {
    data,
    type: SET_APP_READY,
  };
};

export const setUsingHistory = (data): AppAction => {
  return {
    data,
    type: SET_USING_HISTORY,
  };
};

export const setConfig = (data): AppAction => {
  return {
    data,
    type: SET_CONFIG,
  };
};

export const setConfigs = (data): AppAction => {
  return {
    data,
    type: SET_CONFIGS,
  };
};

export const setInspectMode = (data): AppAction => {
  return {
    data,
    type: SET_INSPECT_MODE,
  };
};

export const undo = (data): AppAction => {
  return {
    data,
    type: UNDO,
  };
};

export const redo = (data): AppAction => {
  return {
    data,
    type: REDO,
  };
};

export const setDrawInfoError = (data: { name: string }) => {
  return {
    data,
    type: SET_DRAWINFO_ERROR,
  };
};

export const setEditor = (data?: OLEEditor) => {
  return {
    data,
    type: SET_EDITOR,
  };
};

export const setSelectedPlan = (data): AppAction => {
  return {
    data,
    type: SET_SELECTED_PLAN,
  };
};

export const setParent = (data): AppAction => {
  return {
    data,
    type: SET_PARENT,
  };
};

export const setExportBboxLockedToMap = (data?: boolean): AppAction => {
  return {
    data,
    type: SET_EXPORT_BBOX_LOCKED_TO_MAP,
  };
};

export const setCurrentSidebarWidth = (data?: number): AppAction => {
  return {
    data,
    type: SET_CURRENT_SIDEBAR_WIDTH,
  };
};

export const setCurrentGroupControls = (
  data?: Record<string, boolean>,
): AppAction => {
  return {
    data,
    type: SET_CURRENT_GROUP_CONTROLS,
  };
};

export const setExportType = (data?: string): AppAction => {
  return {
    data,
    type: SET_EXPORT_TYPE,
  };
};

export const setGeolocationActive = (data?: boolean): AppAction => {
  return {
    data,
    type: SET_GEOLOCATION_ACTIVE,
  };
};

export const setExportBtnActive = (data?: boolean): AppAction => {
  return {
    data,
    type: SET_EXPORT_BTN_ACTIVE,
  };
};

export const setOrganization = (data?: Organization) => {
  if (data?.slug) {
    // We want to saved the last used
    localStorage.setItem(LS_ORGANIZATION_KEY, data?.slug ?? "");
  }
  return async (dispatch: Dispatch) => {
    await dispatch(applyConfig(data));
    dispatch({
      data,
      type: SET_ORGANIZATION,
    });
  };
};

export const setOrganizations = (data: Organization[]): AppAction => {
  return {
    data,
    type: SET_ORGANIZATIONS,
  };
};

export const setReadOnlyMode = (data?: boolean): AppAction => {
  return {
    data,
    type: SET_READONLY_MODE,
  };
};

export const fetchDrawInfo = (
  apiUrl: string,
  user: User,
  id?: string,
  uuid?: string,
): Promise<FetchDetailsError | PaginatedResults | PlanInfo> => {
  const url = id
    ? `${apiUrl}/meta/info/${id}/`
    : `${apiUrl}/meta/info/?uuid=${uuid}`;
  return authorizedFetch<FetchDetailsError | PaginatedResults | PlanInfo>(
    user,
    () => {
      return fetchData(url);
    },
  );
};

export const setDrawInfo = (data): AppThunk => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      app: { drawInfo, exportLayoutConfig },
    } = getState();
    if (
      exportLayoutConfig &&
      (!exportLayoutConfig[MAPSET_LAYOUT_TITLE] ||
        exportLayoutConfig[MAPSET_LAYOUT_TITLE] === drawInfo?.name) &&
      data.name
    ) {
      dispatch(
        setExportLayoutConfig({
          [MAPSET_LAYOUT_TITLE]: data.name,
        }),
      );
    }
    dispatch({
      data,
      type: SET_DRAW_INFO,
    });
  };
};

/*
 * Get information of the current KML displayed.
 */
export const getDrawInfo = (uuid) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      app: { apiUrl, kmlAdminId },
      oidc: { user },
    } = getState();
    if (!user || !apiUrl || (!uuid && !kmlAdminId)) {
      return;
    }

    return fetchDrawInfo(apiUrl, user, null, kmlAdminId || uuid)
      .then((data) => {
        const planInfo =
          data &&
          !(data as FetchDetailsError).details &&
          (data as PaginatedResults).results[0];
        if (planInfo && hasRightSetDefaults(user)) {
          document.title = planInfo.name || "mapset";
          dispatch(setDrawInfo(planInfo));
        }
        return planInfo;
      })
      .catch(() => {});
  };
};

const fetchDrawInfos = (
  apiUrl: string,
  user: User,
  params: Record<string, string> = {},
  signal?: AbortSignal,
) => {
  const finalParams = {
    ...params,
    has_name: "true",
    search: params.search ? params.search : "",
  } as Record<string, string>;
  const paramsString = new URLSearchParams(finalParams).toString();

  return authorizedFetch(user, () => {
    return fetchData(
      `${apiUrl}/meta/info/?${paramsString}`,
      "GET",
      null,
      {
        "Accept-Language": document.documentElement.lang,
      },
      signal,
    ).catch((err) => {
      if (err.name === "AbortError") {
        return Promise.resolve();
      }
      throw err;
    });
  });
};

export const getDrawInfos = (params = {}, signal): AppThunk => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      app: { apiUrl, config },
      oidc: { user },
    } = getState();
    if (!user || !apiUrl) {
      return Promise.resolve();
    }
    return fetchDrawInfos(
      apiUrl,
      user,
      { ...params, config: config.slug },
      signal,
    ).then((data) => {
      if (data) {
        dispatch({
          data,
          type: SET_DRAW_INFOS,
        });
      }
      return data;
    });
  };
};

export const deleteDrawInfo = (planId): AppThunk => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      app: { apiUrl },
      oidc: { user },
    } = getState();
    if (!user || !planId) {
      return Promise.resolve();
    }

    return fetch(`${apiUrl}/meta/info/${planId}`, {
      headers: {
        "Accept-Language": document.documentElement.lang,
        "X-CSRFToken": Cookies.get("csrftoken"),
      },
      method: "DELETE",
    }).then((response) => {
      dispatch({ data: null, type: SET_SELECTED_PLAN });
      if (response.ok) {
        void dispatch(refreshMe());
      }
    });
  };
};

/*
 * Create (POST) or update (PUT) plan name in backend and set the draw info in redux
 */
export const upsertDrawInfo = (
  drawInfoToUpsert: Partial<PlanInfo> = {},
  onSaveComplete?: () => void,
) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      app: { apiUrl, config, drawInfo, kmlAdminId },
      oidc: { user },
    } = getState();

    if (!user || !kmlAdminId || (!drawInfoToUpsert && !drawInfo)) {
      return Promise.resolve();
    }

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete("draw.id");

    const deletingDefaultFor = drawInfoToUpsert?.default_for === null;

    const newDrawInfo: Partial<PlanInfo> = {
      ...drawInfo,
      config: config.slug,
      uuid: kmlAdminId,
      ...drawInfoToUpsert,
      default_for: deletingDefaultFor
        ? null
        : ({
            ...(drawInfo?.default_for || {}),
            ...(drawInfoToUpsert?.default_for || {}),
          } as PlanInfo["default_for"]),
    };

    if (!newDrawInfo.name) {
      newDrawInfo.name = "";
    }

    if (
      !newDrawInfo.default_for ||
      (newDrawInfo.default_for &&
        Object.keys(newDrawInfo.default_for).length === 0)
    ) {
      // Sending null value will remove the plan from the list of default plan
      newDrawInfo.default_for = null;
    }

    if (JSON.stringify(newDrawInfo) === JSON.stringify(drawInfo)) {
      return Promise.resolve(drawInfo);
    }
    dispatch(setSaveConfirm(STATE_SAVING));
    return fetch(
      `${apiUrl}/meta/info/${
        drawInfo?.id ? `${drawInfo.id}/?uuid=${drawInfo.uuid}` : ""
      }`,
      {
        body: JSON.stringify(newDrawInfo),
        headers: {
          "Accept-Language": document.documentElement.lang,
          "Content-Type": "application/json",
          "X-CSRFToken": Cookies.get("csrftoken"),
        },
        method: drawInfo?.id ? "PUT" : "POST",
      },
    )
      .then((res) => {
        if (!res.ok) {
          return res.json().then((errorJson) => {
            throw errorJson;
          });
        }
        return res.json();
      })
      .then((data) => {
        if (data?.name) {
          document.title = data.name;
        }

        dispatch(setDrawInfo({ ...data, initialTags: data.tags }));
        dispatch({ data: null, type: SET_DRAWINFO_ERROR });
        dispatch(setSaveConfirm(STATE_SAVED, onSaveComplete));
        if (!drawInfo?.id) {
          void dispatch(refreshMe());
        }
        return data;
      })
      .catch((error) => {
        if (error) {
          dispatch(setSaveConfirm(STATE_SAVE));
          // Don't reset the draw info value, let the user a chance to fix the issues
          // dispatch(setDrawInfo({ ...drawInfo }));
          dispatch({ data: error, type: SET_DRAWINFO_ERROR });
        }
        return Promise.reject(error);
      });
  };
};

export const getOldKml = (id): AppThunk => {
  return (dispatch: Dispatch, getState: GetState) => {
    const headers = {};
    const {
      app: { oldKmlUrl },
    } = getState();
    return fetchData(`${oldKmlUrl}/${id}`, "GET", null, headers)
      .then((data) => {
        dispatch(setFetchError(null));
        if (!data.admin_id) {
          return dispatch({ data, type: GET_KML });
        }
        return data;
      })
      .catch((res) => {
        dispatch({ data: { data: null }, type: GET_KML });
        return Promise.reject(
          dispatch(setFetchError(res.statusText || res.message, GET_KML)),
        );
      });
  };
};

const abortControllersByUuid = {};

export const getKml = (uuid): AppThunk => {
  return (dispatch: Dispatch, getState: GetState) => {
    if (uuid) {
      localStorage.removeItem(LS_KML_KEY);
      const {
        app: { apiUrl, kmlUrl, parentOptions },
        oidc: { user },
      } = getState();

      if (!kmlUrl) {
        return Promise.resolve();
      }

      if (!user && uuid && !getIsPublic(!!parentOptions)) {
        // Throws warning when user tries to load a plan, but he's not logged in and it's not a public plan
        return Promise.resolve(dispatch(setDialogVisible(WARN_DIALOG_NAME)));
      }

      dispatch(setDialogVisible(null));
      const thunk = authorizedFetch(
        user,
        async () => {
          const headers = {
            "Content-Type": "application/json; charset=UTF-8",
          };

          if (abortControllersByUuid[uuid]) {
            abortControllersByUuid[uuid].abort();
          }
          abortControllersByUuid[uuid] = new AbortController();

          try {
            const data = await fetchData(
              `${kmlUrl}/${uuid}/`,
              "GET",
              null,
              headers,
              abortControllersByUuid[uuid].signal,
            );
            const newData = { ...data };
            if (uuid !== newData.read_id) {
              newData.admin_id = uuid;
            } else {
              // if we get the kml using an read_id we delete the admin_id.
              delete newData.admin_id;
            }
            let isOwnedByUser = false;
            if (newData.admin_id && user) {
              const planInfo = await fetchDrawInfo(
                apiUrl,
                user,
                null,
                newData.admin_id,
              ).then((res) => {
                return (
                  !(res as FetchDetailsError)?.details &&
                  (res as PaginatedResults)?.results[0]
                );
              });
              if (planInfo) {
                if (!hasRightSetDefaults(user) && planInfo.default_for) {
                  throw {
                    action: "GET_DEFAULT_PLAN",
                  };
                }
                document.title = planInfo.name || "mapset";
                dispatch(setDrawInfo(planInfo));
              }
              isOwnedByUser = user.email === planInfo?.user;
              if (isOwnedByUser) {
                newData.admin_id = planInfo.uuid;
                dispatch(setReadOnlyMode(false));
              }
            }
            if (user && !isOwnedByUser) {
              const {
                app: { organization },
                oidc: { me },
              } = getState();
              const { isMaxMapsReached } = getMaxMaps(me, organization);
              dispatch(setReadOnlyMode(true));
              dispatch(
                setDialogVisible(
                  isMaxMapsReached
                    ? MAX_MAPS_REACHED_DIALOG_NAME
                    : READ_ONLY_DIALOG_NAME,
                ),
              );
            }

            return dispatch({ data: newData, type: GET_KML });
          } catch (error) {
            if (error.name === "AbortError") {
              // ignore abort error
              return Promise.resolve();
            }
            dispatch({ data: { data: null }, type: GET_KML });
            return Promise.reject(
              dispatch(
                setFetchError(
                  error.statusText || error.message,
                  error.action || GET_KML,
                ),
              ),
            );
          }
        },
        !!parentOptions,
      );
      return thunk;
    }
    const {
      app: { organization },
      oidc: { me, user },
    } = getState();
    if (user && getMaxMaps(me, organization).isMaxMapsReached) {
      dispatch(setReadOnlyMode(true));
      dispatch(setDialogVisible(MAX_MAPS_REACHED_DIALOG_NAME));
    }
    const localKml = localStorage.getItem(LS_KML_KEY);
    return Promise.resolve(
      localKml && dispatch({ data: { data: localKml }, type: GET_KML }),
    );
  };
};

/**
 * This function save a kml string in the current plan.
 * @param {*} kmlString
 * @param {*} drawInfoToUpsert
 * @returns
 */
export const saveKml = (
  kmlString: string,
  drawInfoToUpsert: Partial<PlanInfo>,
  onSaveComplete: () => void,
): AppThunk => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      app: {
        anonymousUserToken,
        kmlAdminId,
        kmlUrl,
        metaKml,
        organization,
        parentOptions,
      },
      oidc: { me, user },
    } = getState();
    if (user && !kmlAdminId && getMaxMaps(me, organization).isMaxMapsReached) {
      dispatch(setSaveConfirm(STATE_SAVE));
      return Promise.resolve();
    }
    dispatch(setSaveConfirm(STATE_SAVING));
    const kml = kmlString || localStorage.getItem(LS_KML_KEY);

    // Define headers
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // If a user is logged or if mapset has been opened by trafimage.
    if (kml && (user || parentOptions)) {
      const urlSuffix = kmlAdminId ? `${kmlAdminId}/` : "";
      // Define Url
      let url = `${kmlUrl}/${urlSuffix}`;

      if (!user && parentOptions) {
        // No user logged and mapset opened by trafimage
        url = `${kmlUrl}/${urlSuffix}`;

        headers.Authorization = `Token ${anonymousUserToken}`;
      }

      // User logged
      if (user && Cookies.get("csrftoken")) {
        headers["X-CSRFToken"] = Cookies.get("csrftoken");
      }

      const searchParams = new URLSearchParams(window.location.search);
      searchParams.delete("draw.id");

      // Define body
      const body = JSON.stringify({
        data: kml,
        queryparams: searchParams.toString(),
      });

      if (
        metaKml?.data &&
        kml === metaKml?.data &&
        searchParams.toString() === metaKml?.queryparams
      ) {
        // no need to save if the kml and queryparms hasn't changed
        dispatch(setSaveConfirm(STATE_SAVED));
        return Promise.resolve();
      }

      const thunk = authorizedFetch(
        user,
        () => {
          return fetchData(url, kmlAdminId ? "PUT" : "POST", body, headers)
            .then((data) => {
              dispatch(setFetchError(null));
              dispatch(upsertDrawInfo(drawInfoToUpsert))
                .then(() => {
                  dispatch(setSaveConfirm(STATE_SAVED, onSaveComplete));
                })
                .catch((err) => {
                  dispatch(setSaveConfirm(STATE_SAVE));
                  // eslint-disable-next-line no-console
                  console.error(err);
                });
              return dispatch({
                data,
                type: SAVE_KML,
              });
            })
            .catch((res) => {
              dispatch(setSaveConfirm(STATE_SAVE));
              return Promise.reject(
                dispatch(
                  setFetchError(res.statusText || res.message, SAVE_KML),
                ),
              );
            });
        },
        !!parentOptions,
      );
      localStorage.removeItem(LS_KML_KEY);
      return thunk;
    }
    if (kmlAdminId && !user) {
      dispatch(setSaveConfirm(STATE_SAVED));
      // Throws warning when user tries to save, but he's not logged in although there is a draw.id
      return dispatch({ data: WARN_DIALOG_NAME, type: "SET_DIALOG_VISIBLE" });
    }
    if (kml) {
      localStorage.setItem(LS_KML_KEY, kml);
      dispatch(setSaveConfirm(STATE_SAVED));
      return Promise.resolve(dispatch({ data: { data: kml }, type: SAVE_KML }));
    }
    dispatch(setSaveConfirm(STATE_SAVED));
    return Promise.resolve();
  };
};

const getDrawCopyIndex = async (
  apiUrl: string,
  user: User,
  originalName: string,
  signal?: AbortSignal,
): Promise<number> => {
  // Select the highest index of all the copies of a plan from the backend
  const promises = ["copy", "Kopie", "copie", "copia"].map((string) => {
    return fetchDrawInfos(
      apiUrl,
      user,
      { search: `${originalName} - ${string}` },
      signal,
    );
  });
  const index = await Promise.all(promises).then((values) => {
    return values.reduce((highestIdx, currentVal) => {
      const currentHighest = currentVal.results.reduce(
        (groupHighestIdx, plan) => {
          const idx = parseInt(
            plan?.name.split(/ - (copy|Kopie|copie|copia) - /)[2],
            10,
          );
          return Number.isInteger(idx) && idx > groupHighestIdx
            ? idx
            : groupHighestIdx;
        },
        0,
      );
      return currentHighest > highestIdx ? currentHighest : highestIdx;
    }, 0);
  });
  return index + 1;
};

// This method will save the current plan defined in the store.
export const savePlan = (
  drawInfoToUpsert: Partial<PlanInfo>,
  onSaveComplete?: () => void,
) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      app: { kmlData },
    } = getState();
    return dispatch(saveKml(kmlData, drawInfoToUpsert, onSaveComplete));
  };
};

export const copyPlan = (
  drawInfoToCopy: Partial<PlanInfo>,
  t: (_key: string) => string,
) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const {
      app: { apiUrl, drawInfo, kmlUrl },
      oidc: { user },
    } = getState();

    const infoToCpy = drawInfoToCopy || drawInfo;
    if (!user) {
      // eslint-disable-next-line no-console
      console.error("You must be logged in to use the copyPlan function");
    }
    if (!infoToCpy) {
      // eslint-disable-next-line no-console
      console.error("The parameter drawInfoToCopy in copyPlan is mandatory");
    }

    const { name, uuid } = infoToCpy;

    const headers = {
      "Content-Type": "application/json",
      "X-CSRFToken": Cookies.get("csrftoken"),
    };

    // Get the kml data to copy (admin_id, read_id, ....)
    const kmlDataToCopy = await authorizedFetch<MetaKml>(user, () => {
      return fetchData(`${kmlUrl}/${uuid}`, "GET", null, headers);
    });

    // Copy and save the kml data.
    const kmlData = (await authorizedFetch<MetaKml>(user, () => {
      return fetchData(
        `${kmlUrl}/`,
        "POST",
        JSON.stringify(kmlDataToCopy),
        headers,
      );
    })) as MetaKml;

    // Copy and save the info metadata
    const [originalName] = name.split(/ - (copy|Kopie|copie|copia) - /);
    const drawInfoIndex = await getDrawCopyIndex(apiUrl, user, originalName);
    const newDrawInfo = {
      ...infoToCpy,
      default_for: null,
      name: `${originalName} - ${t("Kopie")} - ${drawInfoIndex}`,
      uuid: kmlData.admin_id,
    };
    return fetchData(
      `${apiUrl}/meta/info/`,
      "POST",
      JSON.stringify(newDrawInfo),
      headers,
    ).then((data) => {
      void dispatch(refreshMe());
      return data;
    });
  };
};

export const testOpenId = () => {
  const url = `https://geopstest.cloud.tyk.io/openid-test/get`;
  return fetchData(url, "GET", TEST_OPENID);
};

// Export the selected layout as pdf, rendered locally by the backend. The
// MAPSET_LAYOUT_MAP param carries only the read URL of the drawing in print
// mode (see getLayoutScreenshotUrl); the backend builds the actual
// screenshot-service request URL server-side from that plus the
// width/height/scale computed here, so the client never has to know the
// screenshot-service host - only the outer host is a trust boundary.
export const exportLayoutAsPdf = (signal: AbortSignal, readUrl?: string) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const {
      app: { exportLayout, exportLayoutConfig, layoutsUrl },
    } = state;

    if (!exportLayout) {
      return Promise.reject(new Error("No layout to export"));
    }

    const params = new URLSearchParams();

    Object.entries(exportLayout.variables).forEach(([param, { type }]) => {
      if (/(text|html)/.test(type)) {
        params.set(param, exportLayoutConfig[param] || "");
      }
    });

    if (exportLayout.variables[MAPSET_LAYOUT_MAP]) {
      const {
        deviceScaleFactor,
        height,
        readUrl: mapReadUrl,
        width,
      } = await getLayoutScreenshotUrl(state, readUrl);
      params.set(MAPSET_LAYOUT_MAP, mapReadUrl);
      params.set(MAPSET_LAYOUT_MAP_WIDTH, `${width}`);
      params.set(MAPSET_LAYOUT_MAP_HEIGHT, `${height}`);
      params.set(MAPSET_LAYOUT_MAP_SCALE, `${deviceScaleFactor}`);
    }

    try {
      const fetchParams = { signal };
      const url = `${layoutsUrl}/${exportLayout.org}/${exportLayout.name}/pdf/?${params.toString()}`;

      const resp = await fetch(url, fetchParams);
      if (resp.ok) {
        const data = await resp.blob();
        download(
          data,
          exportLayoutConfig[MAPSET_LAYOUT_FILENAME] ??
            exportLayoutConfig[MAPSET_LAYOUT_TITLE],
        );
        return Promise.resolve();
      }

      // On failure the backend returns a json body with the error in a detail property.
      const data = await resp.json().catch(() => {
        return null;
      });
      throw new Error(
        data?.detail ||
          "There was an error on the server. Try again or contact site administrators.",
      );
    } catch (err) {
      if (err?.name !== "AbortError") {
        return Promise.reject(err);
      }
      return Promise.reject();
    }
  };
};

export const writeKmlHistory =
  // usingHistory should come from the store, but it's not updated properly so we pass it in as an argument
  (layer: Layer, usingHistory: boolean): AppThunk => {
    return (dispatch: Dispatch, getState: GetState) => {
      const {
        app: { kmlHistory },
        map: { olMap: map, rotation },
      } = getState();
      if (usingHistory || kmlHistory?.usingHistory) {
        return;
      }
      const newPresentKml = writeKml(layer, map, rotation);
      if (
        (!kmlHistory.present && !kmlHistory.past.length) ||
        kmlHistory.present !== newPresentKml
      ) {
        dispatch(
          setKmlHistory({
            future: [],
            past: [...(kmlHistory?.past || []), kmlHistory?.present].filter(
              Boolean,
            ),
            present: newPresentKml,
          }),
        );
      }
    };
  };

let abortCtrlConfig = new AbortController();
export const getAvailableConfigs = async (
  organization: Organization | undefined,
  state: AppState,
) => {
  const { domain, ssoConfigsUrl } = state;

  let serverConfigs = organization?.available_configs || [];
  if (!serverConfigs?.length && /io|ch/.test(domain)) {
    abortCtrlConfig.abort();
    abortCtrlConfig = new AbortController();
    // Fetch the default config if no user or the user has no configs
    try {
      const response = await fetch(`${ssoConfigsUrl}/mapset${domain}/`, {
        signal: abortCtrlConfig.signal,
      });
      if (response.ok) {
        const data = await response.json();
        serverConfigs = [data];
      } else {
        throw new Error(
          `Failed to fetch default config: ${response.statusText}`,
        );
      }
    } catch (err) {
      if (err && err.name === "AbortError") {
        // ignore user abort request
        return;
      }
      serverConfigs = [];
    }
  }

  const clientConfigs = Object.values(configs);

  const defaultConfig = configs[domain] || configs["ch"];
  if (!defaultConfig) {
    throw new Error(`No default config found for domain: ${domain}`);
  }

  let finalConfigs = serverConfigs
    .map((sConf): AppDomainConfig | null => {
      const clientConf =
        clientConfigs.find((cConf) => {
          return cConf.slug === sConf.slug;
        }) || defaultConfig;

      return clientConf
        ? {
            ...sConf,
            ...clientConf,
            settings: {
              ...((sConf?.settings || {}) as unknown as Settings), // Settings from the backend
              ...(clientConf?.settings || {}), // Here we inject optional local settings
            },
            slug: sConf?.slug,
          }
        : null;
    })
    .filter((conf): conf is AppDomainConfig => {
      return conf !== null;
    })
    .sort((a, b) => {
      return (a.title ?? "").localeCompare(b.title ?? "");
    });
  if (finalConfigs.length === 0) {
    // If no final configs are found, fall back to the default client configs.
    finalConfigs = [defaultConfig];
  }
  return finalConfigs;
};

export const applyConfig = (organization: null | Organization): AppThunk => {
  return async (dispatch: Dispatch, getState: GetState) => {
    /**
     * The CI uses gitlab@geops.de as user. Making alterations in this function
     * or in gitlab user's sso settings at https://sso.geops.io/admin/auth/user/?q=gitlab
     * may impact cypress tests which load a plan with text features, since the font type
     * depends on the CD.
     */
    const {
      app: { config, domain, globalIcons, iconsUrl, imagesUrl, parentOptions },
      oidc: { user },
    } = getState();
    if (config && parentOptions && /io|ch/.test(domain)) {
      dispatch(setConfig(config));
      return;
    }

    const configs = await getAvailableConfigs(organization, getState().app);
    if (!configs) {
      // A superseded request was aborted, nothing to apply.
      return;
    }
    dispatch(setConfigs(configs));

    // Find the localstorage CD and check if user has access to it
    const localStorageSlug = localStorage.getItem(LS_CD_KEY);
    let newConfig = configs.find((cd) => {
      return cd.slug === localStorageSlug;
    });

    // If localstorage CD is not found use default CD for admin
    if (!newConfig && user?.is_staff) {
      const defaultSlug = `geopstest`;
      newConfig = configs.find((cd) => {
        return cd.slug === defaultSlug;
      });
    }

    // If no default admin config found, use domain default CD
    if (!newConfig) {
      const defaultSlug = `mapset${domain}`;
      newConfig = configs.find((cd) => {
        return cd.slug === defaultSlug;
      });
    }

    // If no default config found use the first one
    if (!newConfig) {
      newConfig = configs[0];
    }

    if (!newConfig) {
      // If no configuration could be determined, abort the process.
      return;
    }

    dispatch(setConfig(newConfig));
    if (user) {
      localStorage.setItem(LS_CD_KEY, newConfig.slug);
    }
    const iconCategories = await getIconCategoriesWithUrl(
      newConfig,
      iconsUrl,
      imagesUrl,
      globalIcons,
    );
    dispatch(setIconCategories(iconCategories));
  };
};

export const setIconCategories = (data: IconCategory[]): AppAction => {
  return {
    data: data,
    type: AppActionType.SetIconCategories,
  };
};
